import { useCallback } from 'react';
import { type DefaultError, useMutation } from '@tanstack/react-query';
import { type SupportedLocale } from '@zep-us/i18n';

import { logger } from '@zep/core-logger';

import { AI_HTTP_CLIENT } from '@/api/ai/client';
import { useLocale } from '@/hooks';
import { useBuildFormContext } from '@/views/build/form/schema';
import { useShowAiErrorAlert } from '@/views/build/hook';

import { useQuizFormField } from '../../hook/useQuizFormField';
import { useQuizControl } from '../../QuizControlProvider';
import {
  useAiGenerateMultipleStatusValue,
  useSetAiGenerateMultipleStatus,
} from '../useAiGenerateMultipleStatus';

export const useAiChoiceListGeneration = () => {
  const [choiceList] = useQuizFormField({
    field: 'multipleData.choiceList',
  });
  const [question, _] = useQuizFormField({
    field: 'question',
  });

  const { setValue, getValues } = useBuildFormContext();

  const { id } = useQuizControl();

  const { setIsGeneratingBulk } = useSetAiGenerateMultipleStatus();
  const { generatingIndexes } = useAiGenerateMultipleStatusValue();

  const { mutateAsync, isPending } = useAiGenerateChoiceListMutation();

  const disabled = isPending || !question || generatingIndexes.length > 0;

  const showAiErrorAlert = useShowAiErrorAlert();
  const { locale } = useLocale();

  const handleGeneratedChoiceList = useCallback(
    async (generatedChoiceList: ChoiceItem[]) => {
      const keyArray = choiceList.map(o => o.key);
      const newChoiceList = choiceList.map((o, index) => {
        const targetChoice = generatedChoiceList[index];
        return {
          key: keyArray[index],
          richText: targetChoice.text ?? o.richText,
          text: targetChoice.text ?? o.text,
          isAnswer: targetChoice.isAnswer,
        };
      });

      //NOTE: 사용자의 실시간 입력이 아닌, 비동기 작업으로 특정 form field를 update할 때에는
      //최신화된 quizList를 기반으로 setValue로 update하는 것이 좋습니다.
      const quizList = getValues(`quizList`);

      const targetQuizIndex = quizList.findIndex(quiz => quiz.id === id);

      if (targetQuizIndex === -1) {
        console.error(`target quiz not found: ${id}`);
        return;
      }

      setValue(
        `quizList.${targetQuizIndex}.multipleData.choiceList`,
        newChoiceList,
      );
    },
    [choiceList, setValue, id, getValues],
  );

  const generate = useCallback(async () => {
    const start = Date.now();
    const generation_count = choiceList.length;
    logger.track('add_multiple_choice_ai_start', {
      location: 'bulk',
      generation_count,
    });
    setIsGeneratingBulk(true);
    try {
      const generatedChoiceList = await mutateAsync({
        locale,
        question,
        choiceList: choiceList.map((o, index) => ({
          key: index,
          text: o.text,
          isAnswer: o.isAnswer,
        })),
        previousOutput: choiceList.map((o, index) => ({
          key: index,
          text: o.text || '',
          isAnswer: o.isAnswer,
        })),
      });
      if (generatedChoiceList) {
        void handleGeneratedChoiceList(generatedChoiceList);
      }
      const duration = (Date.now() - start) / 1000;
      logger.track('add_multiple_choice_ai_end', {
        location: 'bulk',
        duration_seconds: duration,
        generation_count,
      });
    } catch (_) {
      const duration = (Date.now() - start) / 1000;
      logger.track('add_multiple_choice_ai_error', {
        location: 'bulk',
        duration_seconds: duration,
        generation_count,
      });
      showAiErrorAlert();
    } finally {
      setIsGeneratingBulk(false);
    }
  }, [
    locale,
    mutateAsync,
    question,
    setIsGeneratingBulk,
    showAiErrorAlert,
    handleGeneratedChoiceList,
    choiceList,
  ]);

  return {
    generate,
    disabled,
  };
};

export interface ChoiceItem {
  key: number;
  text: string;
  isAnswer: boolean;
}

export interface AiGenerateChoiceListQuery {
  subject?: string;
  schoolType?: string;
  grade?: string;
  question: string;
  choiceList: Omit<ChoiceItem, 'text'>[];
  previousOutput?: ChoiceItem[];
  locale?: SupportedLocale;
}

export const useAiGenerateChoiceListMutation = () => {
  return useMutation<ChoiceItem[], DefaultError, AiGenerateChoiceListQuery>({
    mutationFn: async query => {
      const res = await AI_HTTP_CLIENT.post(
        `/quiz-builder/generate/choice-list`,
        query,
      );
      return res.data;
    },
  });
};
